// frontend-crm/src/App.js
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import { QueryClient, QueryClientProvider } from 'react-query';
import { io } from 'socket.io-client';

import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Pedidos from './pages/Pedidos';
import Mensajeros from './pages/Mensajeros';
import Clientes from './pages/Clientes';
import Finanzas from './pages/Finanzas';
import TrackingMap from './pages/TrackingMap';
import WhatsAppPanel from './components/WhatsAppPanel';
import Layout from './components/Layout';

// SuperAdmin Section
import SuperAdminLayout from './pages/SuperAdmin/SuperAdminLayout';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import TenantsList from './pages/SuperAdmin/TenantsList';
import SuperAdminUsuarios from './pages/SuperAdmin/SuperAdminUsuarios';
import SuperAdminBots from './pages/SuperAdmin/SuperAdminBots';
import SuperAdminStats from './pages/SuperAdmin/SuperAdminStats';
import SuperAdminAppBranding from './pages/SuperAdmin/SuperAdminAppBranding';
import BalancePage from './pages/BalancePage';
import ConsolidatedPage from './pages/ConsolidatedPage';

export const ColorModeContext = React.createContext({ toggleColorMode: () => {} });

export function SuperAdminRoute({ children }) {
  const user = (() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
  })();
  const token = localStorage.getItem('token');

  if (!token) return <Navigate to="/login" replace />;
  if (user.tipo !== 'superadmin') return <Navigate to="/" replace />;
  return children;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false }
  }
});

export const socket = io(process.env.REACT_APP_API_URL || '/');

function App() {
  const storedTheme = localStorage.getItem('themeMode') || 'light';
  const [mode, setMode] = React.useState(storedTheme);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', newMode);
          return newMode;
        });
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#FA5A1E', light: '#FF8A50', dark: '#D04A10' },
          secondary: { main: '#0A1B2A', light: '#1A2B3A', dark: '#050E18' },
          success: { main: '#10B981', light: '#34D399', dark: '#059669' },
          warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
          error: { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
          info: { main: '#3B82F6', light: '#60A5FA', dark: '#2563EB' },
          background: {
            default: mode === 'light' ? '#F8FAFC' : '#0B1121',
            paper: mode === 'light' ? '#FFFFFF' : '#111B2E',
          },
          divider: mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)',
        },
        shape: { borderRadius: 12 },
        typography: {
          fontFamily: '"Inter", "Roboto", "Segoe UI", sans-serif',
          h4: { fontWeight: 700, letterSpacing: '-0.02em' },
          h5: { fontWeight: 700, letterSpacing: '-0.01em' },
          h6: { fontWeight: 600 },
          subtitle1: { fontWeight: 600 },
          body2: { lineHeight: 1.6 },
          caption: { fontSize: '0.75rem', fontWeight: 500 },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: { textTransform: 'none', borderRadius: 10, fontWeight: 600, padding: '8px 20px' }
            }
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 16, backgroundImage: 'none',
                boxShadow: mode === 'light'
                  ? '0 1px 3px 0 rgba(0,0,0,0.06), 0 1px 2px 0 rgba(0,0,0,0.03)'
                  : '0 1px 3px 0 rgba(0,0,0,0.3), 0 1px 2px 0 rgba(0,0,0,0.2)',
                border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
              }
            }
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: 'none',
              }
            }
          },
          MuiTableCell: {
            styleOverrides: {
              root: { borderBottom: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}` },
              head: { fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }
            }
          },
          MuiChip: {
            styleOverrides: {
              root: { fontWeight: 600, borderRadius: 8 }
            }
          },
          MuiTableContainer: {
            styleOverrides: {
              root: {
                borderRadius: 16,
                border: `1px solid ${mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
              }
            }
          },
          MuiDrawer: {
            styleOverrides: {
              paper: { border: 'none' }
            }
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                '& .MuiOutlinedInput-root': {
                  borderRadius: 10,
                }
              }
            }
          }
        }
      }),
    [mode],
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <Router>
            <Routes>
              {/* Auth */}
              <Route path="/login" element={<Login />} />

              {/* ─── SuperAdmin Panel (Layout Propio) ─── */}
              <Route
                path="/superadmin"
                element={
                  <SuperAdminRoute>
                    <SuperAdminLayout />
                  </SuperAdminRoute>
                }
              >
                <Route index element={<SuperAdminDashboard />} />
                <Route path="empresas" element={<TenantsList />} />
                <Route path="usuarios" element={<SuperAdminUsuarios />} />
                <Route path="bots" element={<SuperAdminBots />} />
                <Route path="stats" element={<SuperAdminStats />} />
                <Route path="branding" element={<SuperAdminAppBranding />} />
              </Route>

              {/* ─── CRM Normal (Admin/Mensajero) ─── */}
              <Route path="/" element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="pedidos" element={<Pedidos />} />
                <Route path="mensajeros" element={<Mensajeros />} />
                <Route path="clientes" element={<Clientes />} />
                <Route path="finanzas" element={<Finanzas />} />
                <Route path="tracking" element={<TrackingMap />} />
                <Route path="chat" element={<WhatsAppPanel />} />
                <Route path="balance" element={<BalancePage />} />
                <Route path="consolidated" element={<ConsolidatedPage />} />
              </Route>
            </Routes>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </ColorModeContext.Provider>
  );
}

export default App;